/**
 * Отслеживание смены релиза на сервере (заголовок X-App-Version и/или app/version.json).
 * Эталон — первая увиденная версия во вкладке; при смене вызывается handler (баннер «обновите страницу»).
 */

export type AppReleaseUpgradeHandler = () => void;

let baseline: string | null = null;
let upgradeHandler: AppReleaseUpgradeHandler | null = null;

export function setAppReleaseUpgradeHandler(fn: AppReleaseUpgradeHandler | null): void {
  upgradeHandler = fn;
}

/** Сброс эталона (например для тестов). */
export function resetAppReleaseBaseline(): void {
  baseline = null;
}

export function observeServerRelease(version: string | null | undefined): void {
  if (version == null || typeof version !== 'string') {
    return;
  }
  const v = version.trim();
  if (v === '') {
    return;
  }
  if (baseline === null) {
    baseline = v;
    return;
  }
  if (baseline !== v) {
    baseline = v;
    upgradeHandler?.();
  }
}

/** Axios 1.x отдаёт AxiosHeaders: надёжно читать через .get(), а не headers['x-app-version']. */
function readVersionHeader(headers: unknown): string | undefined {
  if (headers == null || typeof headers !== 'object') {
    return undefined;
  }
  const h = headers as { get?: (name: string) => unknown };
  if (typeof h.get === 'function') {
    const v = h.get('X-App-Version') ?? h.get('x-app-version');
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
  }
  const o = headers as Record<string, unknown>;
  const raw = o['x-app-version'] ?? o['X-App-Version'];
  return typeof raw === 'string' ? raw.trim() : undefined;
}

export function observeServerReleaseFromAxiosHeaders(headers: unknown): void {
  observeServerRelease(readVersionHeader(headers));
}

/**
 * URL-ы для опроса версии. Сначала origin страницы (443 и т.д.), затем `VUE_APP_BE_URL` (например :8443).
 * На проде SPA и API часто разные origin — cross-origin fetch без CORS молча падает; страница и `app/version.json`
 * обычно на одном хосте с `index.html`, поэтому первый запрос надёжнее.
 */
export function getVersionPublicPollUrls(baseUrl: string): string[] {
  const qs = `?_=${Date.now()}`;
  const path = `/local/ajax/version_public.php${qs}`;
  const root = String(baseUrl ?? '').replace(/\/$/, '');
  const out: string[] = [];

  if (typeof window !== 'undefined' && window.location?.origin) {
    out.push(`${window.location.origin}${path}`);
  }

  if (root !== '') {
    const absolute = `${root}${path}`;
    if (!out.includes(absolute)) {
      out.push(absolute);
    }
  }

  if (out.length === 0) {
    out.push(path);
  }

  return out;
}

export async function fetchAndObserveVersionJson(baseUrl: string): Promise<void> {
  const urls = getVersionPublicPollUrls(baseUrl);
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) {
        continue;
      }
      const data = (await res.json()) as { version?: unknown };
      if (data && typeof data.version === 'string') {
        observeServerRelease(data.version);
        return;
      }
    } catch {
      // пробуем следующий URL или остаётся эталон по X-App-Version из axios
    }
  }
}
